import { useId } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

export interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  badge?: ReactNode;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}

/** Label + control + hint/error, wired up with matching ids for accessibility. */
export function Field({
  label,
  hint,
  error,
  required,
  badge,
  children,
  className = '',
}: FieldShellProps): JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`field ${error ? 'field-invalid' : ''} ${className}`.trim()}>
      <div className="field-label-row">
        <label className="field-label" htmlFor={id}>
          {label}
          {required ? <span className="field-required" aria-hidden="true"> *</span> : null}
        </label>
        {badge ? <span className="field-badge">{badge}</span> : null}
      </div>
      {children(id, describedBy)}
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'date' | 'number' | 'search';
  placeholder?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  badge?: ReactNode;
  autoComplete?: string;
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email';
  step?: string;
  className?: string;
}

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
  error,
  required,
  disabled,
  readOnly,
  badge,
  autoComplete,
  inputMode,
  step,
  className,
}: TextFieldProps): JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} required={required} badge={badge} className={className}>
      {(id, describedBy) => (
        <input
          id={id}
          className="input"
          type={type}
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          inputMode={inputMode}
          step={step}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  rows = 3,
  hint,
  error,
  placeholder,
  required,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id, describedBy) => (
        <textarea
          id={id}
          className="input textarea"
          rows={rows}
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  error,
  disabled,
  required,
  badge,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  hint?: string;
  error?: string | null;
  disabled?: boolean;
  required?: boolean;
  badge?: ReactNode;
  /** Rendered as an empty-valued first option, e.g. "All statuses". */
  placeholder?: string;
  className?: string;
}): JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} required={required} badge={badge} className={className}>
      {(id, describedBy) => (
        <select
          id={id}
          className="input select"
          value={value}
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
        >
          {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
  hint,
  disabled,
  warning,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
  warning?: ReactNode;
}): JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="checkbox-field">
      <div className="checkbox-row">
        <input
          id={id}
          type="checkbox"
          className="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={hintId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        />
        <label className="checkbox-label" htmlFor={id}>
          {label}
        </label>
      </div>
      {hint ? (
        <p className="field-hint checkbox-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {warning ? <div className="checkbox-warning">{warning}</div> : null}
    </div>
  );
}

export function ToggleField({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <div className="toggle-field">
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="toggle-input"
        checked={checked}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      />
      <label className="toggle-label" htmlFor={id}>
        <span className="toggle-track" aria-hidden="true">
          <span className="toggle-thumb" />
        </span>
        <span className="toggle-text">{label}</span>
      </label>
    </div>
  );
}

/**
 * Slider plus a number box for exact entry. Both edit the same value, which keeps threshold
 * tuning fast without giving up precision.
 */
export function SliderField({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  suffix = '%',
  hint,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  hint?: string;
  disabled?: boolean;
}): JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="slider-field">
      <div className="field-label-row">
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
        <span className="slider-readout">
          {value}
          {suffix}
        </span>
      </div>
      <div className="slider-row">
        <input
          id={id}
          type="range"
          className="slider"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          aria-describedby={hintId}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value))}
        />
        <input
          type="number"
          className="input slider-number"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          aria-label={`${label} exact value`}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)));
          }}
        />
      </div>
      {hint ? (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Builds <select> options from an enum label map. */
export function enumOptions(labels: Record<number, string>): SelectOption[] {
  return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

/** Parses a select value back into a numeric enum member, or null for the "all" option. */
export function parseEnumValue<T extends number>(value: string): T | null {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? (parsed as T) : null;
}
